import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Desktop: shows once the main buy buttons are scrolled past.
 * Mobile (Polianthes): shows whenever the main buy buttons are not on screen and the
 * footer is not reached, so Add to cart is visible from the first screen. Visibility is
 * recomputed on scroll, resize and any layout change (e.g. images loading).
 *
 * The sticky element sits before the product form in the page, so it can connect before
 * the form exists; the form, buttons and footer are therefore looked up lazily.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {ResizeObserver | null} */
  #layoutObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {HTMLElement | null} */
  #buyButtonsBlock = null;

  /** @type {Element | null} */
  #footer = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {number | null} */
  #mobileTimer = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    // Set up now if the form is already in the page, otherwise once the page has been parsed
    this.#ensureSetup();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#ensureSetup(), { once: true, signal });
    }

    // Mobile: recompute visibility whenever the page scrolls or its layout changes
    window.addEventListener('scroll', this.#queueMobileSync, { passive: true, signal });
    window.addEventListener('resize', this.#queueMobileSync, { signal });
    window.addEventListener('load', () => { this.#ensureSetup(); this.#queueMobileSync(); }, { signal });
    if ('ResizeObserver' in window) {
      this.#layoutObserver = new ResizeObserver(this.#queueMobileSync);
      this.#layoutObserver.observe(document.documentElement);
    }
    this.#queueMobileSync();

    this.#getInitialQuantity();

    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#layoutObserver?.disconnect();
    this.#abortController.abort();
    if (this.#mobileTimer) clearTimeout(this.#mobileTimer);
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /** Finds the product form, buy buttons and footer, and starts the desktop observers once. */
  #ensureSetup() {
    const productForm = this.#getProductForm();
    if (!productForm) return;
    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;
    this.#buyButtonsBlock = /** @type {HTMLElement} */ (buyButtonsBlock);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    this.#footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!this.#buyButtonsIntersectionObserver && this.#footer) this.#setupIntersectionObserver(buyButtonsBlock, this.#footer);
    this.#queueMobileSync();
  }

  /**
   * Sets up the IntersectionObservers for the desktop behaviour
   * @param {Element} buyButtonsBlock
   * @param {Element} footer
   */
  #setupIntersectionObserver(buyButtonsBlock, footer) {
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      if (this.#isMobile()) {
        this.#queueMobileSync();
        return;
      }
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        if (this.#isMobile()) {
          this.#queueMobileSync();
          return;
        }
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
  }

  /** Batches mobile visibility checks (at most one every 50ms). */
  #queueMobileSync = () => {
    if (this.#mobileTimer) return;
    this.#mobileTimer = setTimeout(() => {
      this.#mobileTimer = null;
      this.#syncMobile();
    }, 50);
  };

  /** Mobile: show the bar when the main buy buttons are off screen and the footer is not in view. */
  #syncMobile() {
    if (!this.#isMobile()) return;
    if (!this.#buyButtonsBlock || !this.#buyButtonsBlock.isConnected) {
      this.#buyButtonsBlock = null;
      this.#ensureSetup();
      if (!this.#buyButtonsBlock) return;
    }
    const viewport = window.innerHeight;
    const buttons = this.#buyButtonsBlock.getBoundingClientRect();
    const buttonsInView = buttons.top < viewport && buttons.bottom > 0;
    const footerInView = this.#footer ? this.#footer.getBoundingClientRect().top < viewport : false;
    const shouldShow = !buttonsInView && !footerInView && !this.#isChatActive();
    if (shouldShow && !this.#isStuck) this.#showStickyBar();
    else if (!shouldShow && this.#isStuck) this.#hideStickyBar();
  }

  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) this.#ensureSetup();
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
          const block = productForm.closest('.buy-buttons-block');
          if (block) this.#buyButtonsBlock = /** @type {HTMLElement} */ (block);
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        this.#updateButtonText();
        this.#queueMobileSync();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  #showStickyBar() {
    const stickyBar = this.querySelector('[ref="stickyBar"]');
    if (!stickyBar) return;
    this.#isStuck = true;
    stickyBar.setAttribute('data-stuck', 'true');
  }

  #hideStickyBar() {
    const stickyBar = this.querySelector('[ref="stickyBar"]');
    if (!stickyBar) return;
    this.#isStuck = false;
    stickyBar.setAttribute('data-stuck', 'false');
  }

  /** @returns {boolean} Mobile layout (theme's 750px breakpoint). */
  #isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  /**
   * Checks whether the Shopify Chat is active on the page.
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    quantityNumber.textContent = this.#currentQuantity.toString();

    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
